/**
 * Read-only details of the selected node or arrow (the panel a reader sees),
 * and FlowLinkView, the one link renderer shared with the live canvas cards.
 *
 * Links: an app path, "#anchor" or "?query" opens in the same tab, a web link
 * in a new tab with rel="noopener noreferrer" (canvasModel `opensInNewTab`).
 * The host hears about every click first through `onOpenLink` and takes it
 * over (client-side routing) by returning true. A link to another node of the
 * map selects that node and centres it on the canvas.
 */
import type { ReactElement } from "react";
import { nodeTitle, opensInNewTab } from "./canvasModel.js";
import { IconArrowRight, IconCrosshair, IconExternal, IconLink, KindIcon } from "./icons.js";
import { Section } from "./inspectorFields.js";
import { FLOW_EDGE_KIND_LABEL, FLOW_KIND_LABEL } from "./labels.js";
import { isSafeHref, readText, type FlowEdge, type FlowLink, type FlowMapDoc, type FlowMapLabels, type FlowNode, type FlowTone } from "./model.js";
import { kindTone } from "./table.js";

export interface LinkHandlers {
  /** Rewrites an href before it is rendered (FlowMapProps.resolveHref). */
  resolveHref?: (href: string) => string;
  /** Return true when the host handled the click itself (FlowMapProps.onOpenLink). */
  onOpenLink?: (link: FlowLink, node: FlowNode) => boolean | void;
  /** Select a node and bring it into view. */
  onGoToNode: (id: string) => void;
}

/** The chip colour of an arrow: green for yes, red for no, neutral otherwise. */
function edgeTone(edge: FlowEdge): FlowTone {
  return edge.kind === "yes" ? "success" : edge.kind === "no" ? "danger" : "neutral";
}

export function FlowLinkView({
  link,
  node,
  labels,
  locale,
  handlers,
  className,
  fallback,
}: {
  link: FlowLink;
  node: FlowNode;
  labels: FlowMapLabels;
  locale?: string;
  handlers: LinkHandlers;
  className: string;
  /** Text for a link without a label (e.g. the title of the node it jumps to). */
  fallback?: string;
}): ReactElement | null {
  const text = readText(link.label, locale).trim();
  if (link.href) {
    const resolved = handlers.resolveHref ? handlers.resolveHref(link.href) : link.href;
    const href = isSafeHref(resolved) ? resolved : link.href;
    const external = opensInNewTab(href);
    return (
      <a
        className={className}
        href={href}
        target={external ? "_blank" : undefined}
        rel={external ? "noopener noreferrer" : undefined}
        title={labels.openLink}
        onClick={(e) => {
          // A modified or non-primary click keeps the browser's own meaning (a new
          // tab or window); only a plain click is offered to the host first.
          if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
          if (handlers.onOpenLink?.(link, node) === true) e.preventDefault();
        }}
      >
        {external ? <IconExternal size={12} /> : <IconLink size={12} />}
        <span className="wa-fm__link-text">{text || fallback || link.href}</span>
      </a>
    );
  }
  const target = link.nodeId;
  if (!target) return null;
  return (
    <button
      type="button"
      className={className}
      title={labels.goToNode}
      onClick={() => {
        if (handlers.onOpenLink?.(link, node) === true) return;
        handlers.onGoToNode(target);
      }}
    >
      <IconCrosshair size={12} />
      <span className="wa-fm__link-text">{text || fallback || labels.goToNode}</span>
    </button>
  );
}

function Jump({
  id,
  title,
  labels,
  onGoToNode,
  incoming,
  chip,
}: {
  id: string;
  title: string;
  labels: FlowMapLabels;
  onGoToNode: (id: string) => void;
  incoming?: boolean;
  chip?: { text: string; tone: FlowTone };
}): ReactElement {
  return (
    <button type="button" className="wa-fm__jump" title={labels.goToNode} onClick={() => onGoToNode(id)}>
      <IconArrowRight size={14} className={incoming ? "wa-fm__flip" : undefined} />
      <span className="wa-fm__jump-text">{title}</span>
      {chip ? (
        <span className={`wa-fm-chip wa-fm-chip--tone-${chip.tone}`}>
          <span className="wa-fm-chip__text">{chip.text}</span>
        </span>
      ) : null}
    </button>
  );
}

interface DetailsProps {
  doc: FlowMapDoc;
  labels: FlowMapLabels;
  locale?: string;
}

function ConnectionList({
  label,
  edges,
  incoming,
  byId,
  labels,
  locale,
  onGoToNode,
}: DetailsProps & { label: string; edges: FlowEdge[]; incoming: boolean; byId: Map<string, FlowNode>; onGoToNode: (id: string) => void }): ReactElement {
  return (
    <div className="wa-fm__field">
      <span className="wa-fm__label">{label}</span>
      <ul className="wa-fm__rows">
        {edges.map((e) => {
          const otherId = incoming ? e.source : e.target;
          const other = byId.get(otherId);
          const text = readText(e.label, locale).trim();
          return (
            <li key={e.id}>
              <Jump
                id={otherId}
                title={other ? nodeTitle(other, locale, labels) : labels.untitled}
                labels={labels}
                onGoToNode={onGoToNode}
                incoming={incoming}
                chip={text ? { text, tone: edgeTone(e) } : undefined}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Everything a reader can learn about one node: its text, checklist, links and arrows. */
export function NodeDetails({ doc, node, labels, locale, handlers }: DetailsProps & { node: FlowNode; handlers: LinkHandlers }): ReactElement {
  const byId = new Map(doc.nodes.map((n) => [n.id, n] as const));
  const description = readText(node.description, locale).trim();
  const steps = node.steps ?? [];
  const done = steps.filter((s) => s.done).length;
  const links = node.links ?? [];
  const incoming = doc.edges.filter((e) => e.target === node.id);
  const outgoing = doc.edges.filter((e) => e.source === node.id);
  return (
    <>
      <div className="wa-fm__section">
        <h3 className="wa-fm__details-title">{nodeTitle(node, locale, labels)}</h3>
        <div>
          <span className={`wa-fm-chip wa-fm-chip--tone-${kindTone(node.kind, node.tone)}`}>
            <KindIcon kind={node.kind} size={12} />
            <span className="wa-fm-chip__text">{labels[FLOW_KIND_LABEL[node.kind]]}</span>
          </span>
        </div>
        {description ? <p className="wa-fm__details-desc">{description}</p> : null}
      </div>
      {steps.length ? (
        <Section title={labels.steps} aside={<span className="wa-fm__count">{labels.stepsProgress(done, steps.length)}</span>}>
          <ul className="wa-fm__checklist">
            {steps.map((s) => (
              <li key={s.id} className={s.done ? "is-done" : undefined}>
                {/* A disabled, labelled checkbox: a screen reader announces the line and whether it is done. */}
                <label>
                  <input type="checkbox" className="wa-fm__check" checked={s.done === true} readOnly disabled />
                  <span>{readText(s.text, locale)}</span>
                </label>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
      {links.length ? (
        <Section title={labels.links}>
          <div className="wa-fm__links">
            {links.map((l) => {
              const target = l.nodeId ? byId.get(l.nodeId) : undefined;
              return (
                <FlowLinkView
                  key={l.id}
                  link={l}
                  node={node}
                  labels={labels}
                  locale={locale}
                  handlers={handlers}
                  className="wa-fm-node__link"
                  fallback={target ? nodeTitle(target, locale, labels) : undefined}
                />
              );
            })}
          </div>
        </Section>
      ) : null}
      {incoming.length || outgoing.length ? (
        <Section title={labels.connections}>
          {incoming.length ? (
            <ConnectionList
              label={labels.colFrom}
              edges={incoming}
              incoming
              byId={byId}
              doc={doc}
              labels={labels}
              locale={locale}
              onGoToNode={handlers.onGoToNode}
            />
          ) : null}
          {outgoing.length ? (
            <ConnectionList
              label={labels.colTo}
              edges={outgoing}
              incoming={false}
              byId={byId}
              doc={doc}
              labels={labels}
              locale={locale}
              onGoToNode={handlers.onGoToNode}
            />
          ) : null}
        </Section>
      ) : null}
    </>
  );
}

/** A selected arrow for a reader: its label and kind, and the two nodes it joins. */
export function EdgeDetails({
  doc,
  edge,
  labels,
  locale,
  onGoToNode,
}: DetailsProps & { edge: FlowEdge; onGoToNode: (id: string) => void }): ReactElement {
  const title = (id: string): string => {
    const n = doc.nodes.find((x) => x.id === id);
    return n ? nodeTitle(n, locale, labels) : labels.untitled;
  };
  const label = readText(edge.label, locale).trim();
  return (
    <>
      <div className="wa-fm__section">
        {label ? <h3 className="wa-fm__details-title">{label}</h3> : null}
        <div>
          <span className={`wa-fm-chip wa-fm-chip--tone-${edgeTone(edge)}`}>
            <span className="wa-fm-chip__text">{labels[FLOW_EDGE_KIND_LABEL[edge.kind ?? "default"]]}</span>
          </span>
        </div>
      </div>
      <Section title={labels.connections}>
        <div className="wa-fm__field">
          <span className="wa-fm__label">{labels.colFrom}</span>
          <Jump id={edge.source} title={title(edge.source)} labels={labels} onGoToNode={onGoToNode} incoming />
        </div>
        <div className="wa-fm__field">
          <span className="wa-fm__label">{labels.colTo}</span>
          <Jump id={edge.target} title={title(edge.target)} labels={labels} onGoToNode={onGoToNode} />
        </div>
      </Section>
    </>
  );
}
